// =====================================================================================
// INVRT Radar — web console — consent banner designer — queries and mutations
//
// The four routes of docs/API-CONTRACTS-C1.md §4 that the console consumes, each bound to
// its exact server shape (supabase/functions/api/routes/sites.ts):
//
//   GET  /sites/:id/consent        ConsentSettings            (any member)
//   PUT  /sites/:id/banner         ConsentSettings + details? (admin; full replace)
//   GET  /sites/:id/consent/stats  ConsentStats               (any member)
//   POST /sites/:id/consent        {site_id, consent_mode, config}  (admin; mode switch)
//
// The settings query key is shared with the Install tab (whose snippet flips to the loader
// on the first publish), so a publish invalidates it there too.
// =====================================================================================

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import type { ConsentMode } from "@/lib/types";
import type { BannerConfig, ConsentSettings, ConsentStats } from "@/lib/types.c1";

/** PUT /sites/:id/banner answers the GET shape; `details` rides along only when the hosted-banner switch-off failed. */
export interface PublishBannerResponse extends ConsentSettings {
  details?: { hosted_banner?: string; [key: string]: unknown };
  /** Not in the contract today; rendered verbatim if a later server adds advisory lines. */
  warnings?: string[];
}

/** POST /sites/:id/consent */
export interface ConsentModeResponse {
  site_id: string;
  consent_mode: ConsentMode;
  config: Record<string, unknown>;
}

export function consentSettingsKey(orgId: string | null, siteId: string) {
  return ["consent", orgId, siteId] as const;
}

export function consentStatsKey(orgId: string | null, siteId: string, days: number) {
  return ["consent-stats", orgId, siteId, days] as const;
}

export const CONSENT_STATS_DAYS = 30;


export function useConsentSettings(siteId: string): UseQueryResult<ConsentSettings> {
  const api = useApiClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: consentSettingsKey(orgId, siteId),
    queryFn: () => api.get<ConsentSettings>(`/sites/${siteId}/consent`),
    enabled: Boolean(orgId) && siteId !== "",
  });
}

export function useConsentStats(
  siteId: string,
  days: number = CONSENT_STATS_DAYS,
): UseQueryResult<ConsentStats> {
  const api = useApiClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: consentStatsKey(orgId, siteId, days),
    queryFn: () => api.get<ConsentStats>(`/sites/${siteId}/consent/stats`, { query: { days } }),
    enabled: Boolean(orgId) && siteId !== "",
  });
}

export function usePublishBanner(
  siteId: string,
): UseMutationResult<PublishBannerResponse, Error, BannerConfig> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (config: BannerConfig) =>
      api.put<PublishBannerResponse>(`/sites/${siteId}/banner`, config),
    onSuccess: (published) => {
      // The response IS the new settings; the stored copy keeps `details` off the record.
      const { details: _details, warnings: _warnings, ...settings } = published;
      queryClient.setQueryData(consentSettingsKey(orgId, siteId), settings);
      // The Install tab's snippet becomes the loader on the first publish.
      void queryClient.invalidateQueries({ queryKey: ["install", orgId, siteId] });
      void queryClient.invalidateQueries({ queryKey: ["site", orgId, siteId] });
    },
  });
}

export function useSaveConsentMode(
  siteId: string,
): UseMutationResult<ConsentModeResponse, Error, ConsentMode> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (mode: ConsentMode) =>
      api.post<ConsentModeResponse>(`/sites/${siteId}/consent`, { mode }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consentSettingsKey(orgId, siteId) });
      void queryClient.invalidateQueries({ queryKey: ["install", orgId, siteId] });
      void queryClient.invalidateQueries({ queryKey: ["site", orgId, siteId] });
      void queryClient.invalidateQueries({ queryKey: ["sites", orgId] });
    },
  });
}
